import React, { KeyboardEventHandler, useCallback, useEffect, useMemo, useRef } from 'react';
import { functionNoop } from 'src/utils/function/noop';
import debounce from 'lodash/debounce';
import { TextInput } from '../form/text-input';
import { Nullable } from 'src/types/nullable.type';
import classNames from 'classnames';
import { CloseSmallIcon } from 'src/monet/icons';

interface MultiSelectInputProps {
  id: string;
  placeholder?: string;
  values: string[];
  containerClassName?: string;
  validateInput?: (value: string | string[]) => boolean;
  onItemAdd?: (value: string | string[]) => void;
  onItemRemove?: (value: string) => void;
  onInputChange?: (value: string) => void;
  disabled?: boolean;
}

export const MultiSelectInput = ({
  id,
  values,
  placeholder,
  validateInput = () => true,
  onItemAdd = functionNoop,
  onItemRemove = functionNoop,
  onInputChange = functionNoop,
  containerClassName,
  disabled = false,
}: MultiSelectInputProps) => {
  const inputElementRef = useRef<Nullable<HTMLInputElement>>(null);
  const debouncedInputChangeHandler = useMemo(() => {
    return debounce(onInputChange, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inputId = useMemo(() => `MultiSelectInput-${id}`, [id]);

  const validateAndAddInput = (value: string | string[]) => {
    if (!disabled && validateInput(value)) {
      onItemAdd(value);
    }
  };

  const onInputKeyDowned: KeyboardEventHandler = (event) => {
    event.stopPropagation();
    if (inputElementRef.current && event.key === 'Enter') {
      const { value } = inputElementRef.current;
      validateAndAddInput(value);
      inputElementRef.current.value = '';
    }
  };

  const onInputPasted = (event: ClipboardEvent) => {
    if (inputElementRef.current && event.clipboardData) {
      let value = event.clipboardData.getData('text');
      validateAndAddInput(
        value
          .replace(/[\r\n\t]/gm, '|')
          .split('|')
          .filter(Boolean)
      );
      setTimeout(() => {
        inputElementRef.current!.value = '';
      }, 1);
    }
  };

  const onItemRemoveClicked = (value: string) => () => {
    if (!disabled) {
      onItemRemove(value);
    }
  };

  const onInputChanged: KeyboardEventHandler = useCallback((event) => {
    const { value } = event.target as HTMLInputElement;
    debouncedInputChangeHandler(value);
  }, []);

  // Stop the invocation of the debounced function after unmounting
  useEffect(() => {
    return () => {
      debouncedInputChangeHandler.cancel();
    };
  }, [debouncedInputChangeHandler]);

  return (
    <div className={classNames('flex w-full flex-col', containerClassName)}>
      <TextInput
        id={inputId}
        inputProps={{ placeholder, ref: inputElementRef, onKeyDown: onInputKeyDowned, onPaste: onInputPasted }}
        onKeyDown={onInputKeyDowned}
        onChange={onInputChanged}
        disabled={disabled}
      />
      {Boolean(values.length) && (
        <div className='mt-2'>
          <div className='flex flex-wrap -ml-2 -mt-2'>
            {values.map((value, index) => (
              <div
                className='flex items-center btn-bedrock-tertiary-small space-x-1 ml-2 mt-2 text-bedrock-black antialiased'
                key={`${inputId}-item-${index}`}
              >
                <CloseSmallIcon className={`w-5 h-5 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`} onClick={onItemRemoveClicked(value)} />
                <span className='text-bedrock-p'>{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
